import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSpring, animated } from "@react-spring/web";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBolt,
  faUsers,
  faChartLine,
  faExclamationTriangle,
  faClock,
  faArrowUp,
  faArrowDown,
  faHeartbeat,
  faEye,
  faCircle,
  faBell,
  faHistory,
  faFire,
  faTachometerAlt,
  faServer,
  faDatabase,
  faNetworkWired,
} from "@fortawesome/free-solid-svg-icons";
import ActivityCharts from "./ActivityCharts";

const RealTimeDashboard = ({
  onClose,
  metrics: propMetrics,
  alerts: propAlerts,
  activeUsers: propActiveUsers,
}) => {
  const [metrics, setMetrics] = useState(
    propMetrics || {
      changesPerMinute: {},
      changesByType: {},
      changesByField: {},
      recentChanges: [],
    }
  );
  const [activeUsers, setActiveUsers] = useState(propActiveUsers || []);
  const [alerts, setAlerts] = useState(propAlerts || []);
  const [activityLevel, setActivityLevel] = useState(0);

  // Sync with props when they change
  useEffect(() => {
    if (propMetrics) {
      setMetrics(propMetrics);
    }
  }, [propMetrics]);

  useEffect(() => {
    if (propAlerts) {
      setAlerts(propAlerts);
    }
  }, [propAlerts]);

  useEffect(() => {
    if (propActiveUsers) {
      setActiveUsers(propActiveUsers);
    }
  }, [propActiveUsers]);

  // Calculate activity level and other metrics
  useEffect(() => {
    const calculateActivityLevel = () => {
      const now = new Date();
      const currentMinute = `${now.getHours()}:${now.getMinutes()}`;
      const changesThisMinute = metrics.changesPerMinute[currentMinute] || 0;
      const level = Math.min(changesThisMinute * 10, 100);
      setActivityLevel(level);
    };

    const interval = setInterval(calculateActivityLevel, 1000);
    return () => clearInterval(interval);
  }, [metrics]);

  // Calculate additional metrics
  const calculateMetrics = () => {
    const now = new Date();
    let totalChangesToday = 0;
    let peakHour = { hour: 0, changes: 0 };
    let averageResponseTime = 0;

    // Calculate today's total changes
    for (let hour = 0; hour <= now.getHours(); hour++) {
      let hourTotal = 0;
      for (let minute = 0; minute < 60; minute++) {
        const key = `${hour}:${minute}`;
        const changes = metrics.changesPerMinute[key] || 0;
        hourTotal += changes;
        totalChangesToday += changes;
      }
      if (hourTotal > peakHour.changes) {
        peakHour = { hour, changes: hourTotal };
      }
    }

    // Calculate change velocity (changes per minute average)
    const last60Minutes = [];
    for (let i = 0; i < 60; i++) {
      const time = new Date(now - i * 60000);
      const key = `${time.getHours()}:${time.getMinutes()}`;
      last60Minutes.push(metrics.changesPerMinute[key] || 0);
    }
    const avgChangesPerMinute = last60Minutes.reduce((a, b) => a + b, 0) / 60;

    return {
      totalChangesToday,
      peakHour,
      avgChangesPerMinute: avgChangesPerMinute.toFixed(2),
      activeFields: Object.keys(metrics.changesByField || {}).length,
    };
  };

  const additionalMetrics = calculateMetrics();

  // Render metric card
  const renderMetricCard = (
    icon,
    title,
    value,
    color = "blue",
    trend = null
  ) => (
    <motion.div
      whileHover={{ scale: 1.05 }}
      className="bg-white rounded-lg shadow p-4"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-500 uppercase">{title}</p>
          <p className={`text-2xl font-bold text-${color}-600 mt-1`}>{value}</p>
          {trend && (
            <p className="text-xs text-gray-500 mt-1 flex items-center">
              <FontAwesomeIcon
                icon={trend > 0 ? faArrowUp : faArrowDown}
                className={`mr-1 ${
                  trend > 0 ? "text-green-500" : "text-red-500"
                }`}
              />
              {Math.abs(trend)}% vs last hour
            </p>
          )}
        </div>
        <FontAwesomeIcon
          icon={icon}
          className={`text-${color}-500 text-2xl opacity-50`}
        />
      </div>
    </motion.div>
  );

  // Render recent changes with more detail
  const renderRecentChanges = () => {
    return (
      <div className="bg-white rounded-lg shadow h-full">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center">
            <FontAwesomeIcon icon={faHistory} className="mr-2 text-blue-600" />
            Recent Activity Feed
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Live updates • {metrics.recentChanges.length} changes tracked
          </p>
        </div>
        <div
          className="p-4 space-y-3 overflow-y-auto"
          style={{ maxHeight: "calc(100vh - 300px)" }}
        >
          <AnimatePresence>
            {metrics.recentChanges.map((change, index) => (
              <motion.div
                key={change.id}
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className={`p-4 rounded-lg border-l-4 ${
                  change.severity === "high"
                    ? "border-red-500 bg-red-50"
                    : change.type === "insert"
                    ? "border-green-500 bg-green-50"
                    : "border-blue-500 bg-blue-50"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <span className="text-sm font-bold text-gray-900">
                        {change.bookingCode}
                      </span>
                      <span
                        className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                          change.type === "insert"
                            ? "bg-green-100 text-green-700"
                            : change.type === "update"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {change.type}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700">{change.message}</p>

                    {change.changedFields &&
                      change.changedFields.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-500 mb-1">
                            Modified fields:
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {change.changedFields.map((field) => (
                              <span
                                key={field}
                                className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded"
                              >
                                {field}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                    {change.kpiChanges &&
                      Object.keys(change.kpiChanges).length > 0 && (
                        <div className="mt-2 p-2 bg-white rounded">
                          <p className="text-xs text-gray-500 mb-1">
                            KPI Impact:
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            {Object.entries(change.kpiChanges).map(
                              ([kpiName, kpiChange]) => (
                                <div
                                  key={kpiName}
                                  className="flex items-center text-xs"
                                >
                                  <span className="font-medium">
                                    {kpiName}:
                                  </span>
                                  <FontAwesomeIcon
                                    icon={
                                      kpiChange.trend === "up"
                                        ? faArrowUp
                                        : faArrowDown
                                    }
                                    className={`mx-1 ${
                                      kpiChange.trend === "up"
                                        ? "text-red-500"
                                        : "text-green-500"
                                    }`}
                                  />
                                  <span>{kpiChange.current.label}</span>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-xs text-gray-400">
                      {new Date(change.timestamp).toLocaleTimeString()}
                    </p>
                    {change.user && (
                      <p className="text-xs text-gray-500 mt-1">
                        {change.user}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {metrics.recentChanges.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <FontAwesomeIcon icon={faClock} className="text-3xl mb-3" />
              <p>No recent activity</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="bg-gray-50 rounded-xl shadow-2xl w-full h-[95vh] overflow-hidden"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#003366] to-blue-700 text-white p-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold flex items-center">
                <FontAwesomeIcon icon={faBolt} className="mr-3" />
                Real-Time Activity Monitor
              </h2>
              <p className="text-blue-200 mt-1">
                Live system activity and performance metrics
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-200 text-2xl transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="p-4 h-[calc(100%-80px)] overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-full">
            {/* Left Column - Metrics and Charts */}
            <div className="lg:col-span-2 space-y-4 overflow-y-auto pr-2">
              {/* Key Metrics Row */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {renderMetricCard(
                  faTachometerAlt,
                  "Activity Level",
                  `${activityLevel}%`,
                  activityLevel > 70
                    ? "red"
                    : activityLevel > 40
                    ? "yellow"
                    : "green"
                )}
                {renderMetricCard(
                  faDatabase,
                  "Today's Changes",
                  additionalMetrics.totalChangesToday,
                  "blue"
                )}
                {renderMetricCard(
                  faChartLine,
                  "Avg Changes/Min",
                  additionalMetrics.avgChangesPerMinute,
                  "blue"
                )}
                {renderMetricCard(
                  faUsers,
                  "Active Users",
                  activeUsers.length,
                  "green"
                )}
              </div>

              {/* Additional Metrics Row */}
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                {renderMetricCard(
                  faClock,
                  "Peak Hour",
                  `${additionalMetrics.peakHour.hour}:00`,
                  "sky"
                )}
                {renderMetricCard(
                  faServer,
                  "Active Fields",
                  additionalMetrics.activeFields,
                  "blue"
                )}
                {renderMetricCard(
                  faBell,
                  "Critical Alerts",
                  alerts.length,
                  alerts.length > 0 ? "red" : "gray"
                )}
              </div>

              {/* Charts Section */}
              <ActivityCharts metrics={metrics} />

              {/* Active Users and Alerts Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Active Users */}
                <div className="bg-white rounded-lg shadow p-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                    <FontAwesomeIcon
                      icon={faUsers}
                      className="mr-2 text-green-500"
                    />
                    Active Users ({activeUsers.length})
                  </h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {activeUsers.map((user, index) => (
                      <motion.div
                        key={user.socketId}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center">
                          <div className="relative">
                            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs">
                              {user.name?.charAt(0) || "U"}
                            </div>
                            <FontAwesomeIcon
                              icon={faCircle}
                              className="absolute -bottom-1 -right-1 text-green-500 text-xs"
                            />
                          </div>
                          <span className="ml-2 text-sm">
                            {user.name || "Unknown"}
                          </span>
                        </div>
                        {user.currentBooking && (
                          <span className="text-xs text-gray-500">
                            <FontAwesomeIcon icon={faEye} className="mr-1" />
                            Viewing
                          </span>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Critical Alerts */}
                <div className="bg-white rounded-lg shadow p-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                    <FontAwesomeIcon
                      icon={faBell}
                      className="mr-2 text-red-500"
                    />
                    Critical Alerts
                  </h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    <AnimatePresence>
                      {alerts.slice(0, 5).map((alert, index) => (
                        <motion.div
                          key={`${alert.bookingId}-${alert.timestamp}`}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.8 }}
                          className="p-2 bg-red-50 border border-red-200 rounded"
                        >
                          <p className="text-xs font-medium text-red-800">
                            {alert.bookingCode}
                          </p>
                          <p className="text-xs text-red-600">
                            {alert.message}
                          </p>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                    {alerts.length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4">
                        No critical alerts
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Recent Changes Feed */}
            <div className="lg:col-span-1 h-full">{renderRecentChanges()}</div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default RealTimeDashboard;
